import type { ReactNode } from 'react';
import type { ApiProblem } from '../lib/problem.js';
import { Alert, Button } from './ui.js';

/**
 * Renders an `ApiProblem`.
 *
 * One component for every failure the console can display, so that the
 * "send me the request id" instruction is always accompanied by the request id,
 * and so that a 403 caused by a last-owner guard is worded differently from a 403
 * caused by a missing permission. Those two are both `403` and the reader's next
 * move is completely different in each case, so the mapping from `code` to
 * sentence lives here rather than in seven pages.
 *
 * The raw `code` and `requestId` are shown in a small monospace footer. They are
 * not decoration: they are what a person pastes into a support thread, and a UI
 * that hides them makes every bug report start with "what does the network tab
 * say".
 */

/**
 * Business codes that deserve a specific explanation rather than the API's.
 *
 * Every key here must be a code the API actually emits — they are listed in
 * `docs/API.md` §10, and a key that no longer matches is a sentence that silently
 * stops appearing. The API's own `detail` is written to be read by a human, so a
 * miss here degrades to something still useful rather than to a blank.
 */
const EXPLANATIONS: Record<string, string> = {
  last_owner:
    'This organisation would be left without an owner. Promote someone else to owner first, or transfer ownership.',
  not_owner:
    'Only an owner can transfer ownership. Ownership cannot be delegated, because granting it would let the grantee grant it to themselves.',
  use_transfer_ownership:
    'Ownership can only move through the transfer action. Promote the member to admin first, then transfer ownership to them.',
  insufficient_rank:
    'That member is at or above your own role. You cannot change a peer, and you cannot grant a role at or above your own.',
  email_taken: 'That email address already has an account. Sign in instead, or use a different one.',
  invalid_credentials: 'That email and password combination did not match an account.',
  refresh_token_reused:
    'This session was revoked because the refresh token had already been used. That usually means a token was copied. Sign in again and change your password.',
  orphaned_api_key:
    'The account that created this API key no longer exists. Revoke the key and create a new one.',
  slug_taken: 'That slug is already in use. Try another.',
  invitation_pending: 'An invitation is already pending for that address. Revoke it first to send a new one.',
  invitation_email_mismatch:
    'This invitation was sent to a different email address. Sign in as that address to accept it.',
  no_scopes_granted:
    'None of the scopes you selected are held by your account here, so the key would be unable to do anything.',
  idempotency_key_reused:
    'This request reused a key that was already used for different content. Reload the page and try again.',
  idempotency_key_in_flight: 'An identical request is already in progress. Wait a moment and retry.',
  account_missing: 'This account has been disabled or deleted. Contact an organisation owner.',
};

export function ProblemAlert({
  problem,
  title,
  onRetry,
  retryLabel = 'Try again',
  compact = false,
}: {
  problem: ApiProblem;
  title?: string;
  onRetry?: () => void;
  retryLabel?: string;
  compact?: boolean;
}): ReactNode {
  const explanation = EXPLANATIONS[problem.code];

  // 429 gets a countdown rather than a retry button: the retry will fail for
  // exactly as long as the window lasts, and a button that always fails is worse
  // than no button.
  const rateLimited = problem.isRateLimited;

  return (
    <Alert
      tone={problem.isRateLimited ? 'warning' : 'danger'}
      title={title ?? defaultTitle(problem)}
      {...(onRetry && !rateLimited ? { actions: <Button size="sm" variant="secondary" onClick={onRetry}>{retryLabel}</Button> } : {})}
    >
      <p>{explanation ?? problem.userMessage()}</p>

      {!compact ? (
        <p className="mt-2 font-mono text-[0.6875rem] text-ink-subtle">
          <span className="break-anywhere">{problem.code}</span>
          {problem.requestId ? (
            <>
              {' · request '}
              <span className="break-anywhere">{problem.requestId}</span>
            </>
          ) : null}
        </p>
      ) : null}
    </Alert>
  );
}

/**
 * A title derived from the status.
 *
 * Deliberately generic. The API's `title` is an HTTP phrase ("Unprocessable
 * Entity"), which is accurate and useless to a reader. The sentence that actually
 * explains the problem is in `detail`, and this is the one-line summary above it.
 */
function defaultTitle(problem: ApiProblem): string {
  if (problem.isNetworkFailure) return 'Could not reach the API';
  if (problem.isRateLimited) return 'Too many requests';
  if (problem.isAuthFailure) return 'Your session is no longer valid';
  if (problem.isForbidden) return 'You do not have permission to do that';
  if (problem.isNotFound) return 'Not found';
  if (problem.isConflict) return 'That conflicts with something that already exists';
  if (problem.status === 413) return 'That file is too large';
  if (problem.status === 422) return 'Some of the details need fixing';
  if (problem.status >= 500) return 'The API hit an error';
  return 'Something went wrong';
}

/**
 * A field-level message from a problem's `errors[]`.
 *
 * Falls back to the generic detail so a form never shows an empty error under a
 * field that the API rejected for a reason it did not attribute to a field.
 */
export function fieldMessage(problem: ApiProblem | null, field: string): string | undefined {
  if (!problem) return undefined;
  return problem.fieldErrors()[field];
}
